import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { apiClient, apiErrorMessage } from "../api/client";
import type { ImportOutcome, ImportRecord, Job } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell, SortHeader } from "../components/ListShell";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { jobSummary } from "../components/JobProgress";
import { RecordLink } from "../components/RecordLink";
import { Badge, type Tone } from "../ui/Badge";
import { FORMAT_WORDS } from "../lib/importRead";
import { orAbsent, shortDate } from "../lib/format";

/* The records one import touched, each with what the import did to it.
 *
 * James, 2026-10-13, after the first load from the Import screen: a person
 * should be able to click through from an import to the records it added,
 * matched and updated, and open each one, for every kind of import. Every
 * import is one signal in the engine with a link to each record it touched,
 * so this reads api.import_record and api.import_outcome (engine migration
 * 230) for one job, the way /captures/<id> reads one capture's records. */

const KIND_WORDS: Record<string, string> = {
  company: "Company", person: "Person", property: "Property", project: "Project",
};
const KIND_FILTER: [string, string][] = [
  ["company", "Companies"], ["person", "People"], ["property", "Properties"], ["project", "Projects"],
];
/* A project has no record screen in this release, so it shows as its name. */
const LIST: Record<string, "companies" | "people" | "properties"> = {
  company: "companies", person: "people", property: "properties",
};
const DID: Record<string, { label: string; tone: Tone }> = {
  added:   { label: "Added",   tone: "ok" },
  updated: { label: "Updated", tone: "ok" },
  matched: { label: "Matched", tone: "plain" },
};
const CSV_DEFAULT = ["entity_type", "label", "address", "outcome", "held", "entity_id"] as const;

function fileOf(job: Job | null | undefined): string {
  const p = (job?.params ?? {}) as Record<string, unknown>;
  const key = typeof p["key"] === "string" ? p["key"] : "";
  return key.split("/").pop() || "a file";
}

function formatOf(job: Job | null | undefined): string {
  const p = (job?.params ?? {}) as Record<string, unknown>;
  const f = typeof p["format"] === "string" ? p["format"] : "";
  return FORMAT_WORDS[f] ?? f;
}

/** One sentence from the counts: how many records, and how many the import
 *  added, updated, matched and left held for review. */
function countsSentence(rows: readonly ImportOutcome[]): string | null {
  const sum = (test: (r: ImportOutcome) => boolean) =>
    rows.filter(test).reduce((n, r) => n + (r.records ?? 0), 0);
  const total = sum(() => true);
  if (!total) return null;
  const parts = (["added", "updated", "matched"] as const)
    .map((o) => [sum((r) => r.outcome === o), DID[o]?.label.toLowerCase() ?? o] as const)
    .filter(([n]) => n > 0)
    .map(([n, word]) => `${n.toLocaleString()} ${word}`);
  const held = sum((r) => Boolean(r.held));
  return `${total.toLocaleString()} records: ${parts.join(", ")}.${held ? ` ${held.toLocaleString()} held for review.` : ""}`;
}

export function ImportRecords({ session }: { session: SignedIn }) {
  const { id = "" } = useParams();
  const token = session.idToken;
  const queryClient = useQueryClient();
  const params = useListParams("label", ["outcome", "entity_type"] as const, true, "import_record");
  const csvColumns = useCsvColumns("import-records", "import_record", CSV_DEFAULT);
  const outcome = params.filters["outcome"] ?? "";
  const kind = params.filters["entity_type"] ?? "";

  const job = useQuery({
    queryKey: ["import", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("job")
        .select("id,kind,status,params,requested_by,result,error,queued_at,finished_at")
        .eq("id", id).eq("kind", "file_import").limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as Job[])[0] ?? null;
    },
  });

  /* The undo of this import, when there was one, because an undone import's
     records are gone and the page has to say so rather than show an empty
     table. */
  const undo = useQuery({
    queryKey: ["import", id, "undo"],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("job")
        .select("id,status,result,finished_at")
        .eq("kind", "undo_import").eq("params->>job_id", id)
        .order("queued_at", { ascending: false, nullsFirst: false }).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as Job[])[0] ?? null;
    },
  });

  const counts = useQuery({
    queryKey: ["import-records", id, "counts"],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("import_outcome")
        .select("entity_type,outcome,held,records")
        .eq("job_id", id).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ImportOutcome[];
    },
  });

  const narrowed = (columns?: string, opts?: { count?: "exact" }) => {
    let q = apiClient(token).from("import_record").select(columns ?? "*", opts).eq("job_id", id);
    if (outcome === "held") q = q.eq("held", true);
    else if (outcome) q = q.eq("outcome", outcome);
    if (kind) q = q.eq("entity_type", kind);
    if (params.search.trim()) q = q.ilike("label", `*${params.search.trim()}*`);
    return q;
  };

  const query = useQuery({
    queryKey: ["import-records", id, params.page, params.sort, params.ascending, params.filters, params.search],
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await narrowed(
        "job_id,signal_id,entity_type,entity_id,label,address,outcome,held", { count: "exact" })
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        // Ties by id, so a page turn never shows a record twice or not at all.
        .order("entity_id", { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as ImportRecord[], total: count ?? null };
    },
  });

  const rows = query.data?.rows ?? [];
  const undone = undo.data?.status === "done";
  const sentence = countsSentence(counts.data ?? []);

  const heading = job.isLoading ? <p className="muted">Loading the import.</p>
    : job.isError ? <p className="refusal">{(job.error as Error).message}</p>
    : !job.data ? <p className="refusal">No import has this id.</p>
    : (
      <>
        <p>
          <strong>{fileOf(job.data)}</strong>
          <span className="muted"> · {formatOf(job.data)} · asked by {orAbsent(job.data.requested_by)}, {shortDate(job.data.finished_at ?? job.data.queued_at)}</span>
        </p>
        {jobSummary(job.data).length ? (
          <p className="muted">{jobSummary(job.data).join(" ")}</p>
        ) : null}
        {job.data.status === "failed" ? <p className="refusal">This import failed. {job.data.error}</p> : null}
        {undone ? (
          <p>
            <Badge tone="quiet">Undone</Badge>
            <span className="muted"> {shortDate(undo.data?.finished_at)}. Records it made were removed and values it changed went back.</span>
          </p>
        ) : null}
        {sentence ? <p>{sentence}</p> : null}
        {job.data.status === "done" && !undone ? (
          <p><Link to={`/imports/${id}/compare`}>Compare with a campaign</Link>
            <span className="muted"> to see which of these properties are on its list, and why the others are not.</span></p>
        ) : null}
      </>
    );

  const empty = undone ? "This import was undone, so no records are linked to it."
    : job.data?.status === "failed" ? "This import failed, so it touched no records."
    : job.data && job.data.status !== "done" ? "This import has not finished."
    : "No records are linked to this import.";

  return (
    <ListShell
      title="Records from an import"
      view="import_record"
      searchLabel="Record name"
      params={params}
      csvColumns={csvColumns}
      total={query.data?.total ?? null}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching || counts.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => {
        queryClient.invalidateQueries({ queryKey: ["import", id] });
        queryClient.invalidateQueries({ queryKey: ["import-records", id] });
      }}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: `import-${fileOf(job.data).replace(/\.[^.]+$/, "")}`,
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await narrowed(csvColumns.chosen.join(","))
              .order(params.sort, { ascending: params.ascending, nullsFirst: false })
              .order("entity_id", { ascending: true })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as unknown as ImportRecord[];
          },
        })
      }
      emptyEverything={empty}
      beforeList={
        <>
          <p><Link to="/import">Back to Import</Link></p>
          {heading}
        </>
      }
      filterLabels={{ outcome: "What the import did", entity_type: "Kind" }}
      filterWords={{
        outcome: (v) => v === "held" ? "Held for review" : DID[v]?.label ?? v,
        entity_type: (v) => KIND_FILTER.find(([k]) => k === v)?.[1] ?? v,
      }}
      filters={
        <>
          <label htmlFor="f-did">What the import did</label>
          <select id="f-did" value={outcome}
                  onChange={(e) => params.set({ filters: { ...params.filters, outcome: e.target.value }, page: 1 })}>
            <option value="">everything</option>
            {Object.entries(DID).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            <option value="held">Held for review</option>
          </select>
          <label htmlFor="f-kind">Kind</label>
          <select id="f-kind" value={kind}
                  onChange={(e) => params.set({ filters: { ...params.filters, entity_type: e.target.value }, page: 1 })}>
            <option value="">any</option>
            {KIND_FILTER.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </>
      }
    >
      <table>
        <thead>
          <tr>
            <SortHeader column="entity_type" label="Kind" params={params} />
            <SortHeader column="label" label="Record" params={params} />
            <th>Address</th>
            <SortHeader column="outcome" label="What the import did" params={params} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const list = LIST[r.entity_type ?? ""];
            const did = DID[r.outcome ?? ""];
            return (
              <tr key={`${r.entity_type}-${r.entity_id}`}>
                <td>{KIND_WORDS[r.entity_type ?? ""] ?? r.entity_type}</td>
                <td>
                  {list
                    ? <RecordLink to={list} id={r.entity_id} label={r.label ?? r.address} />
                    : orAbsent(r.label)}
                </td>
                <td className="muted">{r.entity_type === "property" ? orAbsent(r.address) : ""}</td>
                <td>
                  {did ? <Badge tone={did.tone}>{did.label}</Badge> : orAbsent(r.outcome)}
                  {r.held ? <> <Badge tone="waiting">Held for review</Badge></> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ListShell>
  );
}
